"use client";

import { SlidersHorizontal } from "lucide-react";

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { t } from "@/lib/localization";
import {
    DIVIDER_SPACINGS,
    DIVIDER_THICKNESSES,
    DividerAppearance,
    DividerBlock,
} from "@/lib/utils/lomdaV3/types";
import { useLomdaV3Editor } from "../../store/useLomdaV3Editor";
import { resolveDividerAppearance } from "../../lib/blockRegistry";
import { DEFAULT_THEME } from "../../lib/theme";
import {
    AppearanceReset,
    AppearanceRow,
    ColorChipField,
    PopoverTitle,
    SegmentedToggle,
    ToolbarDivider,
    WidthControl,
    chromeBtn,
    chromeBtnOpen,
    popoverShell,
} from "./blockAppearance";

export default function DividerSettings({ block }: { block: DividerBlock }) {
    const patchBlockData = useLomdaV3Editor((s) => s.patchBlockData);
    const theme = useLomdaV3Editor((s) => s.metadata.theme) ?? DEFAULT_THEME;
    const update = (patch: DividerAppearance) =>
        patchBlockData(block.id, "divider", (d) => ({
            appearance: { ...d?.appearance, ...patch },
        }));
    // Confirming the theme Border unchanged must not freeze it as a fixed colour.
    const setColor = (color: string) =>
        patchBlockData(block.id, "divider", (d) => {
            const current = resolveDividerAppearance(d?.appearance).color;
            const unchanged =
                current === undefined
                    ? color.toLowerCase() === theme.border.toLowerCase()
                    : color === current;
            return unchanged
                ? null
                : { appearance: { ...d?.appearance, color } };
        });
    const reset = () =>
        patchBlockData(block.id, "divider", (d) =>
            d?.appearance === undefined ? null : { appearance: undefined }
        );

    return (
        <div className="flex items-center gap-3">
            <WidthControl blockId={block.id} />
            <ToolbarDivider />
            <AppearancePopover
                appearance={resolveDividerAppearance(block.data?.appearance)}
                themeBorder={theme.border}
                onChange={update}
                onColor={setColor}
                onReset={reset}
            />
        </div>
    );
}

function AppearancePopover({
    appearance,
    themeBorder,
    onChange,
    onColor,
    onReset,
}: {
    appearance: ReturnType<typeof resolveDividerAppearance>;
    themeBorder: string;
    onChange: (patch: DividerAppearance) => void;
    onColor: (color: string) => void;
    onReset: () => void;
}) {
    const title = t("lomda-v3-editor.divider.appearance");
    const colorLabel = t("lomda-v3-editor.divider.color");

    return (
        <Popover>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    className={cn(chromeBtn, chromeBtnOpen)}
                >
                    <SlidersHorizontal className="size-5" />
                    {title}
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="start"
                className={cn(popoverShell, "w-[320px]")}
            >
                <div className="flex flex-col gap-4">
                    <PopoverTitle>{title}</PopoverTitle>

                    <AppearanceRow
                        label={t("lomda-v3-editor.divider.thickness")}
                    >
                        <SegmentedToggle
                            label={t("lomda-v3-editor.divider.thickness")}
                            value={appearance.thickness}
                            onChange={(thickness) => onChange({ thickness })}
                            options={DIVIDER_THICKNESSES.map((v) => ({
                                value: v,
                                content: t(
                                    `lomda-v3-editor.divider.thickness.${v}`
                                ),
                            }))}
                        />
                    </AppearanceRow>

                    <AppearanceRow label={colorLabel}>
                        <ColorChipField
                            label={colorLabel}
                            value={appearance.color ?? themeBorder}
                            onChange={onColor}
                        />
                    </AppearanceRow>

                    <AppearanceRow label={t("lomda-v3-editor.divider.spacing")}>
                        <SegmentedToggle
                            label={t("lomda-v3-editor.divider.spacing")}
                            value={appearance.spacing}
                            onChange={(spacing) => onChange({ spacing })}
                            options={DIVIDER_SPACINGS.map((v) => ({
                                value: v,
                                name:
                                    v === "none"
                                        ? undefined
                                        : t(
                                              `lomda-v3-editor.divider.spacing.${v}-name`
                                          ),
                                content: t(
                                    `lomda-v3-editor.divider.spacing.${v}`
                                ),
                            }))}
                        />
                    </AppearanceRow>

                    <AppearanceReset onReset={onReset} />
                </div>
            </PopoverContent>
        </Popover>
    );
}
